import { HttpErrorResponse } from '@angular/common/http';

type ApiErrorBody = {
  readonly message?: unknown;
  readonly fields?: Record<string, unknown> | null;
  readonly localization?: Record<string, unknown> | null;
};

type ParseApiErrorOptions = {
  readonly lang?: string;
  readonly includeFields?: boolean;
};

export function parseApiError(error: unknown, options?: ParseApiErrorOptions): string | null {
  const body = error instanceof HttpErrorResponse ? error.error : error;

  if (!isApiErrorBody(body)) {
    return null;
  }

  const lang = normalizeApiErrorLang(options?.lang);
  const includeFields = options?.includeFields ?? true;
  const localization = getLocalizedMessage(body.localization, lang) ?? getString(body.message);

  if (!includeFields) {
    return localization;
  }

  const fieldMessages = collectFieldMessages(body.fields);

  if (fieldMessages.length === 0) {
    return localization;
  }

  return dedupeMessages(localization ? [localization, ...fieldMessages] : fieldMessages).join(' ');
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (!isRecord(value)) {
    return false;
  }

  return isRecord(value['localization']) || typeof value['message'] === 'string' || isRecord(value['fields']);
}

function getLocalizedMessage(localization: Record<string, unknown> | null | undefined, lang: 'ru' | 'en' | 'ky') {
  if (!localization) {
    return null;
  }

  const keys = lang === 'ky' ? ['ky', 'kg', 'KY', 'KG'] : [lang, lang.toUpperCase()];

  for (const key of keys) {
    const message = getString(localization[key]);

    if (message) {
      return message;
    }
  }

  return getString(localization['ru']) ?? getString(localization['RU']);
}

function collectFieldMessages(fields: Record<string, unknown> | null | undefined): string[] {
  if (!fields) {
    return [];
  }

  return Object.values(fields)
    .map((value) => getString(value))
    .filter((value): value is string => value !== null);
}

function dedupeMessages(messages: readonly string[]): string[] {
  return [...new Set(messages)];
}

function normalizeApiErrorLang(lang: string | undefined): 'ru' | 'en' | 'ky' {
  switch (lang?.toLowerCase()) {
    case 'en':
      return 'en';
    case 'kg':
    case 'ky':
      return 'ky';
    case 'ru':
    default:
      return 'ru';
  }
}

function getString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const message = value.trim();

  return message || null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
